import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchMedicationStatsSummary, fetchMedicationsByTab } from '../../services/medicationService';
import MedicationTable from '../MedicationTable';

const MedicationOverviewTab = ({ serverId }) => {
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState('today');
    
    const [medications, setMedications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    // Fetch top level stats
    useEffect(() => {
        const loadStats = async () => {
            try {
                const data = await fetchMedicationStatsSummary(serverId);
                setStats(data);
            } catch (err) {
                console.error("Failed to load medication stats", err);
            }
        };
        loadStats();
    }, [serverId]);

    // Fetch tab data
    const loadTabData = useCallback(async (isLoadMore = false) => {
        try {
            setIsLoading(true);
            const { medications: newMeds, lastVisible: newLastVisible } = await fetchMedicationsByTab(
                serverId, 
                activeTab, 
                isLoadMore ? lastVisible : null, 
                20
            );
            
            if (isLoadMore) {
                setMedications(prev => [...prev, ...newMeds]);
            } else {
                setMedications(newMeds);
            }
            
            setLastVisible(newLastVisible);
            setHasMore(newMeds.length === 20); // Basic check for next page
        } catch (err) {
            console.error("Failed to load medications", err);
        } finally {
            setIsLoading(false);
        }
    }, [serverId, activeTab, lastVisible]);

    // Reset and reload when tab changes
    useEffect(() => {
        setMedications([]);
        setLastVisible(null);
        setHasMore(false);
        loadTabData(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, serverId]);

    const handleLoadMore = () => {
        if (!isLoading && hasMore) {
            loadTabData(true);
        }
    };

    // Calculate dynamic stats from current page if server stats are empty
    const dynamicStats = useMemo(() => {
        let administered = 0;
        let missed = 0;
        let refused = 0;
        
        medications.forEach(med => {
            if (med.timing && med.timing.length > 0) {
                const anyGiven = med.timing.some(t => t.given === true);
                const anyRefused = med.timing.some(t => t.administrationStatus && t.administrationStatus.includes('Refused'));
                
                if (anyGiven) {
                    administered++;
                } else if (anyRefused) {
                    refused++;
                } else if (activeTab === 'missed' || activeTab === 'previous') {
                    missed++;
                }
            }
        });
        
        return {
            administered: stats?.totalTimesAdministered || administered,
            missed: stats?.totalTimesMissed || missed,
            refused: stats?.totalTimesRefused || refused
        };
    }, [medications, stats, activeTab]);

    // Chart Data Preparation
    const pieData = [
        { name: 'Administered', value: dynamicStats.administered, color: '#38a169' },
        { name: 'Missed', value: dynamicStats.missed, color: '#e53e3e' },
        { name: 'Refused', value: dynamicStats.refused, color: '#dd6b20' },
    ];

    return (
        <div>
            {/* Top Level KPIs & Charts */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                
                {/* KPI Cards */}
                <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '20px', minWidth: '300px' }}>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', borderLeft: '4px solid #3182ce' }}>
                        <div style={{ fontSize: '40px', marginRight: '20px' }}>👥</div>
                        <div>
                            <div style={{ fontSize: '14px', color: '#718096', textTransform: 'uppercase', fontWeight: 'bold' }}>Active Consumers</div>
                            <div style={{ fontSize: '28px', color: '#2d3748', fontWeight: 'bold' }}>
                                {stats?.totalActiveConsumer || [...new Set(medications.map(m => m.userID))].length}
                            </div>
                        </div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', borderLeft: '4px solid #805ad5' }}>
                        <div style={{ fontSize: '40px', marginRight: '20px' }}>💊</div>
                        <div>
                            <div style={{ fontSize: '14px', color: '#718096', textTransform: 'uppercase', fontWeight: 'bold' }}>Total Prescriptions</div>
                            <div style={{ fontSize: '28px', color: '#2d3748', fontWeight: 'bold' }}>
                                {stats?.totalTimesPrescribed || medications.length}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chart */}
                <div style={{ flex: '2', backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', minWidth: '400px' }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#4a5568' }}>Agency Adherence Overview</h3>
                    <div style={{ height: '250px', width: '100%', position: 'relative' }}>
                        {(dynamicStats.administered > 0 || dynamicStats.missed > 0 || dynamicStats.refused > 0) ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={pieData.filter(d => d.value > 0)}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {pieData.filter(d => d.value > 0).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="middle" align="right" layout="vertical" />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#a0aec0', flexDirection: 'column' }}>
                                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📊</div>
                                <div>Not enough adherence data to display chart.</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Sub-Tabs (Today, Upcoming, Previous, Discontinued) */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                {['today', 'upcoming', 'previous', 'discontinued'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: activeTab === tab ? '#ebf8ff' : 'transparent',
                            color: activeTab === tab ? '#3182ce' : '#718096',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: activeTab === tab ? 'bold' : 'normal',
                            textTransform: 'capitalize'
                        }}
                    >
                        {tab === 'previous' ? 'Previous / Missed' : `${tab} Medications`}
                    </button>
                ))}
            </div>

            {/* Data Table */}
            <MedicationTable 
                medications={medications} 
                isLoading={isLoading} 
                onLoadMore={handleLoadMore} 
                hasMore={hasMore} 
            />
        </div>
    );
};

export default MedicationOverviewTab;
